/**
 * Toolbar — Base UI Toolbar primitive (filter / action rows).
 *
 * Design system mapping:
 *   • Root: flex items-center gap-3 flex-wrap
 *   • Group: inline-flex items-center gap-2
 *   • Separator: h-6 w-px bg-webex-gray-300 dark: webex-gray-700
 *
 * Composition:
 *   <Toolbar.Root aria-label="Filter">
 *     <Toolbar.Group>
 *       <Input leadingIcon={<SearchIcon />} placeholder="Search…" />
 *     </Toolbar.Group>
 *     <Toolbar.Separator />
 *     <Toolbar.Group>
 *       <Toolbar.Button render={<Button variant="secondary">Filters</Button>} />
 *     </Toolbar.Group>
 *   </Toolbar.Root>
 */
import * as React from "react"
import { Toolbar as BaseToolbar } from "@base-ui-components/react/toolbar"
import { cn } from "@lib/cn"

const Root = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseToolbar.Root>
>(({ className, ...props }, ref) => (
  <BaseToolbar.Root
    ref={ref}
    className={cn("flex items-center gap-3 flex-wrap", className)}
    {...props}
  />
))
Root.displayName = "Toolbar.Root"

const Group = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseToolbar.Group>
>(({ className, ...props }, ref) => (
  <BaseToolbar.Group
    ref={ref}
    className={cn("inline-flex items-center gap-2", className)}
    {...props}
  />
))
Group.displayName = "Toolbar.Group"

const Separator = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseToolbar.Separator>
>(({ className, ...props }, ref) => (
  <BaseToolbar.Separator
    ref={ref}
    className={cn("h-6 w-px bg-webex-gray-300 dark:bg-webex-gray-700 mx-1", className)}
    {...props}
  />
))
Separator.displayName = "Toolbar.Separator"

export const Toolbar = {
  Root,
  Group,
  Separator,
  Button: BaseToolbar.Button,
  Link: BaseToolbar.Link,
  Input: BaseToolbar.Input,
}
